/**
 * 管理员审计日志管理相关 Store
 * 负责审计日志数据的加载、管理和操作
 */

import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import { useCacheStore } from '../cache'
import { adminAuditLogsApi } from '@/https'
import type { PaginationData } from '@/https/request'
import type {
    AuditLog,
    AuditLogListRequest,
    AuditLogFilters,
    UserActivityRequest,
    UserActivityResponse,
    AuditLogAction,
    AuditLogResult
} from '@/https/types_admin/audit_logs'

export const useAdminAuditLogsStore = defineStore('admin-audit-logs', () => {
    const cacheStore = useCacheStore()

    // ===== 状态管理 =====
    const auditLogList = ref<AuditLog[]>([])
    const currentUserActivity = ref<UserActivityResponse | null>(null)

    // 分页信息
    const auditLogPagination = ref({
        total: 0,
        page: 1,
        size: 20,
        has_more: false
    })

    // 查询参数
    const lastAuditLogQuery = ref<AuditLogListRequest>({})
    const currentFilters = ref<AuditLogFilters>({})

    // 加载状态
    const auditLogListLoading = ref(false)
    const userActivityLoading = ref(false)

    // 错误状态
    const auditLogListError = ref<string | null>(null)
    const userActivityError = ref<string | null>(null)

    // ===== 计算属性 =====

    // 按操作类型分组的日志
    const logsByAction = computed(() => {
        const groups: Partial<Record<AuditLogAction, AuditLog[]>> = {}
        auditLogList.value.forEach(log => {
            const list = groups[log.action] ?? (groups[log.action] = [])
            list.push(log)
        })
        return groups
    })

    // 按结果分组的日志
    const logsByResult = computed(() => {
        const groups: Partial<Record<AuditLogResult, AuditLog[]>> = {}
        auditLogList.value.forEach(log => {
            const list = groups[log.result] ?? (groups[log.result] = [])
            list.push(log)
        })
        return groups
    })

    // 日志统计
    const auditLogStats = computed(() => {
        const total = auditLogList.value.length
        const success = auditLogList.value.filter(log => log.result === 'success').length
        const failed = auditLogList.value.filter(log => log.result === 'failed').length
        const denied = auditLogList.value.filter(log => log.result === 'denied').length

        return {
            total,
            success,
            failed,
            denied,
            success_rate: total > 0 ? Math.round((success / total) * 100) : 0
        }
    })

    // 最近操作的用户
    const recentOperators = computed(() => {
        type AuditUser = NonNullable<AuditLog['user']>
        const operators = new Map<number, { user: AuditUser; count: number; recent: string }>()

        auditLogList.value.forEach(log => {
            if (log.user) {
                const existing = operators.get(log.user.id)
                if (existing) {
                    existing.count += 1
                    if (log.created_at > existing.recent) {
                        existing.recent = log.created_at
                    }
                } else {
                    operators.set(log.user.id, {
                        user: log.user,
                        count: 1,
                        recent: log.created_at
                    })
                }
            }
        })

        return Array.from(operators.values())
            .sort((a, b) => b.count - a.count)
            .slice(0, 10)
    })

    // ===== 审计日志列表操作 =====

    /**
     * 加载审计日志列表
     */
    const loadAuditLogList = async (params: AuditLogListRequest = {}, forceRefresh = false) => {
        const cacheKey = `admin-audit-logs-list-${JSON.stringify(params)}`

        // 检查缓存
        if (!forceRefresh) {
            const cached = cacheStore.getCache(cacheKey)
            if (cached) {
                const data = cached as PaginationData<AuditLog>
                auditLogList.value = data.items
                auditLogPagination.value = {
                    total: data.total,
                    page: data.page,
                    size: data.size,
                    has_more: data.has_more
                }
                return data
            }
        }

        auditLogListLoading.value = true
        auditLogListError.value = null

        try {
            const response = await adminAuditLogsApi.getAuditLogList(params)

            auditLogList.value = response.items
            auditLogPagination.value = {
                total: response.total,
                page: response.page,
                size: response.size,
                has_more: response.has_more
            }
            lastAuditLogQuery.value = params

            // 保存当前过滤条件
            if (params.filters) {
                currentFilters.value = params.filters
            }

            // 缓存结果
            cacheStore.setCache(cacheKey, response, 5 * 60 * 1000) // 5分钟缓存

            return response
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : '加载审计日志失败'
            auditLogListError.value = message
            throw err
        } finally {
            auditLogListLoading.value = false
        }
    }

    /**
     * 获取用户活动记录
     */
    const loadUserActivity = async (params: UserActivityRequest, forceRefresh = false) => {
        const cacheKey = `admin-user-activity-${JSON.stringify(params)}`

        if (!forceRefresh) {
            const cached = cacheStore.getCache(cacheKey)
            if (cached) {
                currentUserActivity.value = cached as UserActivityResponse
                return cached
            }
        }

        userActivityLoading.value = true
        userActivityError.value = null

        try {
            const response = await adminAuditLogsApi.getUserActivity(params)
            currentUserActivity.value = response

            // 缓存结果
            cacheStore.setCache(cacheKey, response, 3 * 60 * 1000) // 3分钟缓存

            return response
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : '加载用户活动记录失败'
            userActivityError.value = message
            throw err
        } finally {
            userActivityLoading.value = false
        }
    }

    /**
     * 搜索审计日志
     */
    const searchAuditLogs = async (filters: AuditLogFilters) => {
        return loadAuditLogList({ filters, page: 1 }, true)
    }

    /**
     * 按操作类型筛选
     */
    const filterByAction = async (action: AuditLogAction) => {
        return loadAuditLogList({ filters: { action }, page: 1 }, true)
    }

    /**
     * 按结果筛选
     */
    const filterByResult = async (result: AuditLogResult) => {
        return loadAuditLogList({ filters: { result }, page: 1 }, true)
    }

    /**
     * 按时间范围筛选
     */
    const filterByDateRange = async (startDate: string, endDate: string) => {
        return loadAuditLogList({
            filters: { start_date: startDate, end_date: endDate },
            page: 1
        }, true)
    }

    /**
     * 按用户筛选
     */
    const filterByUser = async (userId: number) => {
        return loadAuditLogList({ filters: { user_id: userId }, page: 1 }, true)
    }

    /**
     * 清除审计日志缓存
     */
    const clearAuditLogCache = () => {
        cacheStore.removeCacheByPrefix('admin-audit-logs-')
        cacheStore.removeCacheByPrefix('admin-user-activity-')
    }

    /**
     * 重置状态
     */
    const resetAuditLogState = () => {
        auditLogList.value = []
        currentUserActivity.value = null
        auditLogPagination.value = {
            total: 0,
            page: 1,
            size: 20,
            has_more: false
        }
        lastAuditLogQuery.value = {}
        currentFilters.value = {}

        // 重置错误状态
        auditLogListError.value = null
        userActivityError.value = null

        // 重置加载状态
        auditLogListLoading.value = false
        userActivityLoading.value = false
    }

    // ===== 工具方法 =====

    /**
     * 根据ID查找审计日志
     */
    const findAuditLogById = (id: number): AuditLog | undefined => {
        return auditLogList.value.find(log => log.id === id)
    }

    /**
     * 获取操作类型选项
     */
    const getActionChoices = () => {
        return adminAuditLogsApi.getActionChoices()
    }

    /**
     * 获取操作结果选项
     */
    const getResultChoices = () => {
        return adminAuditLogsApi.getResultChoices()
    }

    /**
     * 格式化日期时间
     */
    const formatDateTime = (dateTime: string) => {
        return new Date(dateTime).toLocaleString('zh-CN', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        })
    }

    /**
     * 获取操作类型的颜色标签
     */
    const getActionColor = (action: AuditLogAction): string => {
        const colorMap: Record<AuditLogAction, string> = {
            'grant_role': 'blue',
            'revoke_role': 'orange',
            'grant_permission': 'green',
            'revoke_permission': 'red',
            'check_permission': 'default'
        }
        return colorMap[action] || 'default'
    }

    /**
     * 获取结果的颜色标签
     */
    const getResultColor = (result: AuditLogResult): string => {
        const colorMap: Record<AuditLogResult, string> = {
            'success': 'success',
            'failed': 'error',
            'denied': 'warning'
        }
        return colorMap[result] || 'default'
    }

    return {
        // 状态
        auditLogList,
        currentUserActivity,
        auditLogPagination,
        lastAuditLogQuery,
        currentFilters,

        // 加载状态
        auditLogListLoading,
        userActivityLoading,

        // 错误状态
        auditLogListError,
        userActivityError,

        // 计算属性
        logsByAction,
        logsByResult,
        auditLogStats,
        recentOperators,

        // 方法
        loadAuditLogList,
        loadUserActivity,
        searchAuditLogs,
        filterByAction,
        filterByResult,
        filterByDateRange,
        filterByUser,
        clearAuditLogCache,
        resetAuditLogState,

        // 工具方法
        findAuditLogById,
        getActionChoices,
        getResultChoices,
        formatDateTime,
        getActionColor,
        getResultColor
    }
})
